import React, { useState } from 'react';
import DeleteScheduleLineGroupIcon from '@mui/icons-material/EventBusy';
import { Box, Button, DialogContentText, Tooltip } from '@mui/material';

import { closeCurrentPopup, Dialog } from '@paragon/popup-state';

import { deleteScheduleLineGroup, editScheduleLineGroup } from '../../../actions/scheduleLineGroupActions';
import { getStatusesByScheduleLineGroupGuid } from '../../../selectors/scheduleLineGroups';
import { RootState } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ScheduleLineGroupProperties } from './ScheduleLineGroupProperties';

export interface EditScheduleLineGroupDialogProps {
  readonly scheduleLineGroupGuid: string;
}

export function EditScheduleLineGroupDialog(props: EditScheduleLineGroupDialogProps) {
  const { scheduleLineGroupGuid } = props;
  const dispatch = useAppDispatch();
  const scheduleLineGroup = useAppSelector((state: RootState) => state.scheduleLineGroups)[scheduleLineGroupGuid];
  const statusesByScheduleLineGroup = useAppSelector(getStatusesByScheduleLineGroupGuid);

  const [scheduleLineGroupName, setScheduleLineGroupName] = useState<string | null>(scheduleLineGroup.name);
  const [scheduleLineGroupNameError, setScheduleLineGroupNameError] = useState<boolean>(false);
  const [selectedLineGuid, setSelectedLineGuid] = useState<string | null>(scheduleLineGroup.lineGuid);
  const [lineError, setLineError] = useState<boolean>(false);
  const [scheduledDate, setScheduledDate] = useState<string | null>(scheduleLineGroup.scheduledDate);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);

  const someBundlesHaveStatusOtherThanScheduled =
    statusesByScheduleLineGroup[scheduleLineGroupGuid].filter((status) => status !== 'Scheduled').length > 0;

  function edit() {
    if (!selectedLineGuid) {
      setLineError(true);
    }
    if (!scheduleLineGroupName || !selectedLineGuid) return;

    dispatch(editScheduleLineGroup(scheduleLineGroup.guid, scheduleLineGroupName, selectedLineGuid, scheduledDate));
    dispatch(closeCurrentPopup());
  }

  return (
    <Dialog
      title="Edit Schedule Line Group"
      open
      submitText="Edit"
      onSubmit={edit}
      onCancel={() => void dispatch(closeCurrentPopup())}
      contentSx={{ width: '400px', height: '260px' }}
      submitDisabled={scheduleLineGroupName === null || selectedLineGuid === null}
      enterKeySubmits
    >
      <Box sx={{ mb: 2 }}>
        <ScheduleLineGroupProperties
          scheduleLineGroupName={scheduleLineGroupName}
          setScheduleLineGroupName={(newScheduleLineGroupName) => {
            setScheduleLineGroupName(newScheduleLineGroupName);
            if (newScheduleLineGroupName === '') setScheduleLineGroupNameError(true);
          }}
          scheduleLineGroupNameError={scheduleLineGroupNameError}
          selectedLineGuid={selectedLineGuid}
          setSelectedLineGuid={(lineGuid) => {
            setSelectedLineGuid(lineGuid);
            setLineError(false);
          }}
          selectedLineGuidError={lineError}
          scheduledDate={scheduledDate}
          setScheduledDate={setScheduledDate}
        />
      </Box>
      <Tooltip
        disableInteractive
        title={
          someBundlesHaveStatusOtherThanScheduled
            ? 'Some bundles in this schedule line group have a status other than "Scheduled" so it cannot be deleted'
            : 'Delete this schedule line group - its bundles will still be in the schedule group'
        }
      >
        <span>
          <Button
            variant="outlined"
            onClick={() => void setConfirmDeleteOpen(true)}
            startIcon={<DeleteScheduleLineGroupIcon sx={{ color: 'red' }} />}
            disabled={someBundlesHaveStatusOtherThanScheduled}
          >
            Delete
          </Button>
        </span>
      </Tooltip>
      <Dialog
        open={confirmDeleteOpen}
        variant="design"
        title="Confirm Delete"
        onSubmit={() => {
          dispatch(deleteScheduleLineGroup(scheduleLineGroupGuid));
          setConfirmDeleteOpen(false);
          dispatch(closeCurrentPopup());
        }}
        onCancel={() => void setConfirmDeleteOpen(false)}
      >
        <DialogContentText>
          Delete Schedule Line Group &quot;{scheduleLineGroup.name}&quot;? All bundles associated with this Schedule
          Line Group will no longer be associated with it.
        </DialogContentText>
      </Dialog>
    </Dialog>
  );
}
